/**
 * TEACHER PROFILE editor (F3-D): bio/specialties/photo/title/public_slug +
 * is_public toggle, with a live PREVIEW of the public page (reuses
 * `PublicTeacherContent`). Shows the public URL and links to it once published.
 *
 * **Plan 06 · E4:** the record is read and written via REST
 * (`GET|PUT /api/academy/:orgId/teachers/:userId`, "owner or ManageMembers"
 * authz — the same rule the write-path used to apply, moved to the only
 * place that can still enforce it). A third party's record is no longer in
 * the local SQLite, so the local `update … where id` found no row and the
 * edit was lost without a sound.
 */
import { type Component, createMemo, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ExternalLink, Eye, Save } from "@fleet/icons";
import {
  academyPeopleQueryKeys,
  type PublicTeacherPageDTO,
  slugifyPublicSlug,
  type TeacherRecordPatch,
} from "@space/shared";
import { eq, organization } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { PageContainer, PageHeader, Skeleton, Switch } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncSingle } from "@space/shared/solid";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { useActiveOrg } from "../../hooks/use-active-org";
import { useMyOrgRole, useSaveTeacherRecord, useTeacherRecord } from "../../hooks/use-people-data";
import { useOrgIdentities } from "../../hooks/use-org-identities";
import { PublicTeacherContent } from "./public-teacher-page";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

export const TeacherProfileEditor: Component<{ userId: string }> = (props) => {
  const { t } = useI18n();
  const { activeOrgId, activeOrg } = useActiveOrg();
  const profile = useTeacherRecord(activeOrgId, () => props.userId);
  const save = useSaveTeacherRecord(activeOrgId, () => props.userId);
  // D3 (plan 06 · E2): identity from the single source, not from a local join.
  const people = useOrgIdentities(activeOrgId);
  const identity = () => people.identityOf(props.userId);
  const rbac = useMyOrgRole(activeOrgId);
  const org = useOrganizationRow(activeOrgId);

  // The teacher themselves or whoever has ManageMembers can edit. It's the
  // SAME rule `can_write_teacher_record` applies on the server; here it's a
  // courtesy (not showing a form that will 403), there it's the authority.
  //
  // It used to compare `props.userId === identity()?.userId`, which is the
  // identity OF THAT SAME RECORD: always equal, so "I'm the owner" was true
  // for anyone. It's compared against the session's user instead.
  const { userId: sessionUserId } = useSession();
  const canEdit = () => rbac.can("ManageMembers") || props.userId === sessionUserId();

  const p = () => profile.data;
  const orgSlug = () => org.data?.slug ?? "";
  const publicUrl = () =>
    orgSlug() && p()?.publicSlug ? `/teachers/${orgSlug()}/${p()?.publicSlug}` : "";

  // DTO for the live preview (composes local data).
  const previewPage = createMemo<PublicTeacherPageDTO>(() => ({
    org: {
      name: activeOrg()?.name ?? org.data?.name ?? null,
      slug: orgSlug() || null,
      logo: org.data?.logo ?? null,
      branding: null,
    },
    teacher: {
      name: identity()?.name ?? null,
      title: p()?.title ?? null,
      bio: p()?.bio ?? null,
      specialties: specialtiesOf(p()?.specialties),
      photo: p()?.photo ?? null,
      publicSlug: p()?.publicSlug ?? null,
    },
    courses: [],
  }));

  // The patch is SPARSE: what isn't sent isn't touched (same semantics
  // Drizzle's `set()` used to have, which is why the form doesn't change shape).
  const patch = (input: TeacherRecordPatch) => {
    save.mutate(input);
  };

  // Create = an empty PUT: the server's upsert materializes the record with
  // the migration's default values. There's no id to fabricate on the client.
  const create = async () => {
    await save.mutateAsync({});
    showToast(t(msg`Teacher profile created`), "success");
  };

  return (
    <PageContainer maxWidth="5xl">
      <div class="space-y-5">
        <PageHeader
          title={t(msg`Teacher profile`)}
          description={t(msg`Public page shown to prospective students.`)}
          backTo="/academy/people"
          icon={Eye}
        />

        <Show when={!profile.isLoading} fallback={<Skeleton class="h-64 w-full rounded-2xl" />}>
          <Show
            when={p()}
            fallback={
              <div class="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-border p-10 text-center">
                <p class="text-sm text-muted-foreground">
                  {t(msg`This member has no teacher profile yet.`)}
                </p>
                <Show when={canEdit()}>
                  <Button data-testid="teacher-create" onClick={() => void create()}>
                    {t(msg`Create teacher profile`)}
                  </Button>
                </Show>
              </div>
            }
          >
            <div class="grid gap-6 lg:grid-cols-2">
              {/* Form */}
              <fieldset class="space-y-3" disabled={!canEdit()} data-testid="teacher-editor">
                <label class="block space-y-1 text-xs text-muted-foreground">
                  {t(msg`Title`)}
                  <input
                    class={field}
                    data-testid="teacher-title"
                    value={p()?.title ?? ""}
                    onChange={(e) => patch({ title: e.currentTarget.value || null })}
                  />
                </label>
                <label class="block space-y-1 text-xs text-muted-foreground">
                  {t(msg`Bio`)}
                  <textarea
                    class={`${field} min-h-[120px] resize-y`}
                    data-testid="teacher-bio"
                    value={p()?.bio ?? ""}
                    onChange={(e) => patch({ bio: e.currentTarget.value || null })}
                  />
                </label>
                <label class="block space-y-1 text-xs text-muted-foreground">
                  {t(msg`Specialties (comma-separated)`)}
                  <input
                    class={field}
                    data-testid="teacher-specialties"
                    value={specialtiesOf(p()?.specialties).join(", ")}
                    onChange={(e) =>
                      patch({
                        specialties: e.currentTarget.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </label>
                <label class="block space-y-1 text-xs text-muted-foreground">
                  {t(msg`Photo URL`)}
                  <input
                    class={field}
                    data-testid="teacher-photo"
                    value={p()?.photo ?? ""}
                    onChange={(e) => patch({ photo: e.currentTarget.value || null })}
                  />
                </label>
                <label class="block space-y-1 text-xs text-muted-foreground">
                  {t(msg`Public URL slug`)}
                  <input
                    class={field}
                    data-testid="teacher-slug"
                    value={p()?.publicSlug ?? ""}
                    onChange={(e) =>
                      patch({ publicSlug: slugifyPublicSlug(e.currentTarget.value) || null })
                    }
                  />
                </label>

                <div class="flex items-center justify-between rounded-xl border border-border p-3">
                  <div>
                    <p class="text-sm font-semibold">{t(msg`Public profile`)}</p>
                    <p class="text-xs text-muted-foreground">
                      {t(msg`Anyone with the link can view this page.`)}
                    </p>
                  </div>
                  <Switch
                    checked={p()?.isPublic === true}
                    onCheckedChange={(v) => patch({ isPublic: v })}
                    data-testid="teacher-is-public"
                  />
                </div>

                <Show when={p()?.isPublic === true && publicUrl()}>
                  <a
                    href={publicUrl()}
                    target="_blank"
                    rel="noreferrer"
                    class="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                    data-testid="teacher-public-link"
                  >
                    <ExternalLink class="h-4 w-4" /> {t(msg`Open public page`)}
                  </a>
                </Show>

                <Show when={canEdit()}>
                  <div class="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
                    <Save class="h-3.5 w-3.5" /> {t(msg`Changes save automatically.`)}
                  </div>
                </Show>
              </fieldset>

              {/* Live preview */}
              <div class="space-y-2">
                <p class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {t(msg`Live preview`)}
                </p>
                <div
                  class="rounded-2xl border border-border bg-muted/20 p-4"
                  data-testid="teacher-preview"
                >
                  <PublicTeacherContent page={previewPage()} preview />
                </div>
              </div>
            </div>
          </Show>
        </Show>
      </div>
    </PageContainer>
  );
};

/** Local query of the `organization` row (for slug/name/logo in the preview). */
function useOrganizationRow(orgId: () => string | undefined) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  type OrgRow = { name: string | null; slug: string | null; logo: string | null };
  return createPowerSyncSingle<OrgRow>({
    queryKey: () => academyPeopleQueryKeys.orgRow(orgId()),
    db,
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id
        ? d
            .select({ name: organization.name, slug: organization.slug, logo: organization.logo })
            .from(organization)
            .where(eq(organization.id, id))
            .limit(1)
        : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });
}

/**
 * Specialties as an array of strings.
 *
 * E4: the DTO already brings them typed (`jsonb` → array), so the
 * `JSON.parse` with a "comma-separated" fallback that was needed when SQLite
 * stored them as text is gone. What's left is the defensive filter in case
 * an old row's `jsonb` carries something that isn't a string.
 */
function specialtiesOf(raw: string[] | null | undefined): string[] {
  return Array.isArray(raw) ? raw.filter((s): s is string => typeof s === "string") : [];
}
